import { Box, Typography } from "@mui/material";
import { useMemo, useRef, useState, type ReactNode } from "react";
import { EASE, HAIRLINE_SOFT } from "@/components/merch/merchTheme";
import { useThemePalette } from "@/components/backgrounds/useThemePalette";
import useCoverTint from "@/hooks/useCoverTint";
import { cssGlowShadow, glowColors } from "@/utils/releaseGlow";
import { sharedProps } from "@/utils/sharedElements";
import FeaturedReleaseArt from "./FeaturedReleaseArt";
import ReleaseGlow, { type ReleaseGlowMode } from "./glow/ReleaseGlow";

const CARD_RADIUS = 24;

interface FeaturedReleaseCardProps {
  title: string;
  kindLabel: string;
  meta: string[];
  imageUrl?: string;
  fallbackImageUrl?: string;
  playing?: boolean;
  /** Shared element key for the cover and title (the Spotify album id). */
  sharedKey?: string;
  children?: ReactNode;
}

export function FeaturedReleaseCard({ title, kindLabel, meta, imageUrl, fallbackImageUrl, playing = false, sharedKey, children }: FeaturedReleaseCardProps) {
  const tint = useCoverTint(imageUrl);
  const theme = useThemePalette();
  const colors = useMemo(() => glowColors(tint, theme.accent2), [tint, theme.accent2]);
  const hostRef = useRef<HTMLDivElement | null>(null);
  const [glowMode, setGlowMode] = useState<ReleaseGlowMode>("css");
  const baseShadow = tint ? `0 40px 80px -40px color-mix(in srgb, ${tint.glow} 85%, transparent)` : "0 30px 60px -30px rgba(0,0,0,0.7)";

  return (
    <Box
      ref={hostRef}
      className="featured-card"
      data-glow={glowMode}
      sx={{
        position: "relative",
        display: "grid",
        gap: { xs: "18px", md: "18px" },
        p: { xs: "18px", md: "clamp(18px, 1.5rem, 26px)" },
        borderRadius: `${CARD_RADIUS}px`,
        border: HAIRLINE_SOFT,
        overflow: "hidden",
        isolation: "isolate",
        background: tint
          ? `radial-gradient(90% 70% at 15% 0%, color-mix(in srgb, ${tint.glow} 42%, transparent), transparent 65%), linear-gradient(170deg, ${tint.surface} 0%, color-mix(in srgb, var(--color1, #111) 55%, black) 100%)`
          : "linear-gradient(170deg, color-mix(in srgb, var(--color1, #111) 80%, black), color-mix(in srgb, var(--color1, #111) 55%, black))",
        boxShadow: `${cssGlowShadow(colors)}, ${baseShadow}`,
        transition: `box-shadow 0.8s ${EASE}`,
        "&[data-glow='shader']": { boxShadow: baseShadow },
        "@media (prefers-reduced-motion: reduce)": { transition: "none" },
      }}
    >
      <ReleaseGlow hostRef={hostRef} colors={colors} radius={CARD_RADIUS} onModeChange={setGlowMode} />
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px" }}>
        <Typography
          component="span"
          sx={{
            color: "var(--logoColor)",
            fontFamily: "Poppins, sans-serif",
            fontSize: { xs: "13px", md: "12px" },
            fontWeight: 700,
            letterSpacing: "0.12em",
            textTransform: "uppercase",
          }}
        >
          Newest release
        </Typography>
        <Box
          component="span"
          sx={{
            display: "inline-flex",
            alignItems: "center",
            minHeight: "24px",
            px: "10px",
            borderRadius: "999px",
            backgroundColor: "var(--logoColor2)",
            color: "var(--color1, #0b0b0b)",
            fontFamily: "Poppins, sans-serif",
            fontSize: "12px",
            fontWeight: 800,
            letterSpacing: "0.04em",
            textTransform: "uppercase",
          }}
        >
          {kindLabel}
        </Box>
      </Box>
      <Box sx={{ width: { xs: "82%", md: "80%" } }}>
        <FeaturedReleaseArt imageUrl={imageUrl} fallbackImageUrl={fallbackImageUrl} alt={title} playing={playing} sharedKey={sharedKey} />
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography
          component="h3"
          {...sharedProps("title", sharedKey)}
          sx={{
            m: 0,
            width: "fit-content",
            maxWidth: "100%",
            color: "var(--logoColor2)",
            fontFamily: "Poppins, sans-serif",
            fontWeight: 700,
            fontSize: { xs: "26px", md: "clamp(22px, 1.7rem, 32px)" },
            lineHeight: 1.1,
            textWrap: "balance",
          }}
        >
          {title}
        </Typography>
        <Typography sx={{ mt: "6px", color: "var(--logoColor)", opacity: 0.85, fontFamily: "Poppins, sans-serif", fontSize: { xs: "15px", md: "clamp(13px, 0.9rem, 15px)" } }}>
          {meta.filter(Boolean).join(" · ")}
        </Typography>
      </Box>
      {children}
    </Box>
  );
}

export default FeaturedReleaseCard;
